import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';

import GameService from '../../../../../services/game.service';
import TeamStatsBoxList from './statBoxList';
import TeamStatsChart from './statChart';
import { getSeriesData } from '../../../../../common/utilities';
import TeamStatsVideoPlayer from './videoDialog';
import { XmlDataFilterTeamOverview } from '../../../components/xmldatateam';

const TeamStats = ({ games, gameIds, teamId, t }) => {
    const [playerList, setPlayerList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [gameList, setGameList] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [videoOpen, setVideoOpen] = useState(false);
    const [playData, setPlayData] = useState([]);
    const [isOur, setIsOur] = useState(true);
    const [sportcodeOpen, setSportcodeOpen] = useState(false);
    const [exportData, setExportData] = useState([]);

    const { user: currentUser } = useSelector((state) => state.auth);

    const handleChangeTeam = (flag) => {
        setIsOur(flag);
        setPlayerList([]);
    };

    useEffect(() => {
        setLoading(true);
        // Only make API call when we have valid data AND a valid teamId (not -1)
        if (games.length > 0 && gameIds.length > 0 && teamId && teamId > 0) {
            setGameList(games.filter((item) => gameIds.includes(item.id)));

            if (isOur) {
                GameService.getGamePlayerTags(currentUser.id, teamId, null, gameIds.join(','), null, null, null, null, null, null, null, null).then((res) => {
                    setPlayerList(getSeriesData(res));
                    setLoading(false);
                });
            } else {
                GameService.getOpponentTags(currentUser.id, teamId, null, gameIds.join(','), null, null, null, null, null, null, null, null).then((res) => {
                    setPlayerList(getSeriesData(res));
                    setLoading(false);
                });
            }
        } else if (!teamId || teamId <= 0) {
            // teamId not yet loaded, keep loading state
            setLoading(true);
        } else {
            setGameList([]);
            setPlayerList([]);
            setLoading(false);
        }
    }, [games, gameIds, refresh, isOur, teamId]);

    const getList = (series) => {

        if (series) {
            const llst = []
            const slst = []
            const clst = []
            series.forEach(element => {
                if (element?.data > 0) {
                    llst.push(element.name)
                    slst.push(element.data)
                    clst.push(element.color)
                }
            });
            return { seriesDonut: slst, labelsLst: llst, colorLst: clst }
        }
    }
    function getPeriod(id) {
        return id === 1 ? 'H1' : id === 2 ? 'H2' : 'OT';
    }

    const handleClick = (data) => {
        try {
            const newData = data.map((item) => ({
                tag_id: item.id,
                start_time: item.player_tag_start_time,
                end_time: item.player_tag_end_time,
                player_name: item.player_names || item.player_name || item.full_name || '',
                action_name: item.action_names || item.action_name || '',
                action_type: item.action_type_names || item.action_type_name || item.action_type || '',
                action_result: item.action_result_names || item.action_result_name || item.action_result || '',
                game_id: item.game_id,
                team_id: teamId,
                court_area: item.court_area_id,
                inside_pain: item.inside_the_pain,
                period: item.period,
                time_in_game: item.time_in_game,
                home_team_logo: item.home_team_logo,
                away_team_logo: item.away_team_logo,
                home_team_goals: item.home_team_goals ?? item.home_team_goal,
                away_team_goals: item.away_team_goals ?? item.away_team_goal
            }));
            if (newData.length > 0) {
                setVideoOpen(true)
                setPlayData(newData);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleExportSportcode = (data) => {
        if (data && data.length > 0) {
            setExportData(data);
            setSportcodeOpen(true);
        }
    };

    return (
        <Box sx={{ width: '100%', background: '#f8fafc', overflowY: 'auto', padding: '12px 8px', gap: '10px', maxHeight: '79vh', minHeight: '77vh' }}>
            <Box
                sx={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    padding: '10px 20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    marginBottom: '12px'
                }}
            >
                <Box sx={{ display: 'flex', gap: '4px' }}>
                    <Box
                        onClick={() => handleChangeTeam(true)}
                        sx={{
                            padding: '6px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: isOur ? '#6366f1' : '#f3f4f6',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                backgroundColor: isOur ? '#6366f1' : '#e5e7eb'
                            }
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: isOur ? '#ffffff' : '#6b7280'
                            }}
                        >
                            {t('Our Team')}
                        </Typography>
                    </Box>
                    <Box
                        onClick={() => handleChangeTeam(false)}
                        sx={{
                            padding: '6px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: !isOur ? '#6366f1' : '#f3f4f6',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                backgroundColor: !isOur ? '#6366f1' : '#e5e7eb'
                            }
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: !isOur ? '#ffffff' : '#6b7280'
                            }}
                        >
                            {t('Opponent')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
            {loading && (
                <div style={{ width: '100%', height: '70vh', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <Box
                    sx={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                    }}
                >
                    <Box>
                        <TeamStatsBoxList games={gameList} t={t} list={playerList} teamId={teamId} refreshPage={setRefresh} onExportSportcode={handleExportSportcode} />
                    </Box>
                    <Grid container spacing={2}>
                        {playerList.map((stat, index) => (
                            stat.total > 0 && (
                                <Grid item xs={12} sm={6} key={index}>
                                    <TeamStatsChart
                                        className="mb-3"
                                        title={stat.title}
                                        seriesBar={stat.seriesBar}
                                        seriesDonut={getList(stat.seriesBar)}
                                        total={stat.total}
                                        tags={stat.tags}
                                        handleClick={handleClick}
                                        onExportSportcode={handleExportSportcode}
                                        t={t}
                                    />
                                </Grid>
                            )
                        ))}
                    </Grid>
                </Box>
            )}

            <TeamStatsVideoPlayer
                t={t}
                open={videoOpen}
                onClose={(flag) => {
                    setVideoOpen(false);
                    if (flag) setRefresh((r) => !r);
                }}
                video_url={gameList}
                tagList={playData}
            />
            {sportcodeOpen && (
                <XmlDataFilterTeamOverview
                    games={gameList}
                    tagList={exportData}
                    isOur={isOur}
                    tag_name="Team Stats"
                    team_name="TeamStats"
                    setExportXML={setSportcodeOpen}
                />
            )}
        </Box>
    );
};

export default TeamStats;
